import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import type { ComponentProps } from 'react'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import type { Option } from 'components/OfferList/OfferListFilter/OfferListFilterUtils'
import {
  filterItemsToOptions,
  generateOptionItems,
} from 'components/OfferList/OfferListFilter/OfferListFilterUtils'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import {
  deleteSearchParams,
  toggleSearchParamValue,
} from 'lib/url/searchUrlUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Modal from 'LuxKit/Modal/Modal'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import LayoutContainer from 'components/Common/LayoutContainer'
import { toggleSetValue } from 'lib/set/setUtils'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import Group from 'components/utils/Group'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'

interface Props {
  mapOverlay?: boolean
  availableFilters: Array<App.FilterItem> | undefined
  filterOrder?: Record<string, number>
  label: string
  selectedFilters: Array<string>
  search: string
  searchQueryKey: string
  onFilterChange: FilterControlChangeHandler
  filterChipSize?: ComponentProps<typeof FilterChip>['size']
}

function FilterDropdown(props: Props) {
  const { availableFilters, filterOrder, selectedFilters, label, search, searchQueryKey, onFilterChange, filterChipSize = 'medium', mapOverlay = false } = props
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const [isDropdownOpen, toggleDropDown, , closeDropdown] = useToggle()
  const dispatch = useAppDispatch()
  const filterChipRef = useRef<HTMLButtonElement>(null)
  const loading = !availableFilters

  const [options, setOptions] = useState<Array<Option>>([])
  const [selectedSet, setSelectedSet] = useState(new Set<string>())
  // Hide counts here, as in the map's All filters panel.
  const optionItems = useMemo(() => generateOptionItems(options, selectedSet, { hideCounts: mapOverlay }), [options, selectedSet, mapOverlay])

  useEffect(() => {
    setSelectedSet(new Set(selectedFilters))
  }, [selectedFilters])

  useEffect(() => {
    if (!availableFilters || !filterOrder) {
      return // still loading
    }

    if (availableFilters.length > 0) {
      const newOptions = filterItemsToOptions(availableFilters, filterOrder)
      setOptions(newOptions)
    }
  }, [availableFilters, filterOrder, dispatch, search, searchQueryKey, selectedSet])

  const onFilterTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSet(toggleSetValue(selectedSet, e.target.value))

    onFilterChange({ search: toggleSearchParamValue(search, searchQueryKey, e.target.value) })
  }, [onFilterChange, search, searchQueryKey, selectedSet])

  const onClose = useCallback(() => {
    closeDropdown()
  }, [closeDropdown])

  const onReset = useCallback(() => {
    setSelectedSet(new Set())
    dispatch(replace({ search: deleteSearchParams(search, searchQueryKey) }))
  }, [dispatch, search, searchQueryKey])

  const hasAvailableFilters = !!options.length

  const resetText = intl.formatMessage({
    id: 'iHQWrOPSDCyUDX97STWw',
    defaultMessage: 'Reset',
  })

  const viewOffersText = intl.formatMessage({
    id: 'E3gq0DDBC8eIZiZ-hSRq',
    defaultMessage: 'View Offers',
  })

  const noAvailableFiltersText = intl.formatMessage({
    id: 'dtgTspToD4irYL2_gpwH',
    defaultMessage: 'No available filters',
  })

  const resetAllText = intl.formatMessage({
    id: '-9lzAJJnRIrInHwDay4s',
    defaultMessage: 'Reset all',
  })

  const resetLabel = resetAllText + (selectedSet.size ? ` (${selectedSet.size})` : '')
  const chipLabel = label + (selectedSet.size ? ` (${selectedSet.size})` : '')

  const modalTitle = useMemo(() => {
    return <Group direction="horizontal" verticalAlign="center" gap={8}>
      {label}
      {loading ? <LoadingAnimation size={8} /> : null}
    </Group>
  }, [label, loading])

  const filterContent = <>
    {!hasAvailableFilters && <BodyText variant="medium">{noAvailableFiltersText}</BodyText>}
    {hasAvailableFilters && <FilterPanelCheckboxGroup
      name={label}
      items={optionItems}
      onChange={onFilterTypeChange}
      maxItems={5}
      allowShowMore={false}
    />}
  </>

  return (
    <div>
      <FilterChip aria-label={mapOverlay ? chipLabel : undefined} data-map-filter-trigger={mapOverlay || undefined} ref={filterChipRef} selected={!!selectedSet.size} size={filterChipSize} onClick={toggleDropDown} endIcon={isDropdownOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}>
        {chipLabel}
      </FilterChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          data-testid={mapOverlay ? 'hotel-map-filter-dropdown' : undefined}
          data-map-filter-dropdown={mapOverlay || undefined}
          autoHeight={mapOverlay || undefined}
          title={mapOverlay ? label : undefined}
          size="M"
          triggerRef={filterChipRef}
          anchorRef={filterChipRef}
          open={isDropdownOpen}
          placement="bottom-start"
          onClose={onClose}
          secondaryActionProps={{
            kind: 'tertiary',
            children: resetText,
            onClick: onReset,
          }}
          primaryActionProps={{
            children: viewOffersText,
            onClick: onClose,
          }}
        >
          <LayoutContainer>
            {mapOverlay ? filterContent : (
              <FilterPanelFilterGroup allowToggle={false} showDropdown={false} title={label} loading={loading} showBorder={false}>
                {filterContent}
              </FilterPanelFilterGroup>
            )}
          </LayoutContainer>
        </DropdownList>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <Modal
          title={modalTitle}
          isOpen={isDropdownOpen && isMobile}
          onClose={onClose}
          primaryActionText={viewOffersText}
          onPrimaryActionClick={onClose}
          secondaryActionText={resetLabel}
          onSecondaryActionClick={onReset}
        >
          {filterContent}
        </Modal>
      </CSSBreakpoint>
    </div>
  )
}

export default FilterDropdown
